import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import InvoiceDetailsBillingAddress, {
    InvoiceDetailsBillingAddressStyles,
} from "@insite/content-library/Widgets/InvoiceDetails/InvoiceDetailsBillingAddress";
import InvoiceDetailsShippingAddress, {
    InvoiceDetailsShippingAddressStyles,
} from "@insite/content-library/Widgets/InvoiceDetails/InvoiceDetailsShippingAddress";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import OrderDetailPageTypeLink from "@tKgpco/Overrides/Components/OrderDetailPageTypeLink";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import Select, { SelectProps } from "@insite/mobius/Select";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import updateDiscrepancyStatus from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/UpdateDiscrepancyStatus";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import loadDiscrepancyDetailsCollection from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/loadDiscrepancyDetailsCollection";
import updateRMAStatusHandler from "@tKgpco/Store/Data/RMA/Handlers/UpdateRMAStatus";
import loadRMADetailsCollection from "@tKgpco/Store/Data/RMA/Handlers/loadRMADetailsCollection";
import loadRMACollection from "@tKgpco/Store/Data/RMA/Handlers/loadRMACollection";
import Plus from "@insite/mobius/Icons/Plus";
import Trash2 from "@insite/mobius/Icons/Trash2";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Button from "@insite/mobius/Button";

const mapStateToProps = (state: CustomApplicationState) => ({
    language: state.context.session.language,
    rmaDetails: state.KGPState.RMAStore.RMACollectionDetails,
    parameters: state.KGPState.RMAStore.RMACollectionParams,
    featureWidgets: state.context.session.properties.featureWidgets,
    isSecurityRolesAssigned: state.context.session?.properties?.isSecurityRolesAssigned?.toLowerCase() === "true",
});

const mapDispatchToProps = {
    updateDiscrepancyStatus,
    loadRMADetailsCollection,
    updateRMAStatusHandler,
    loadRMACollection,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceDetailsInformationStyles {
    invoiceInformationGridContainer?: GridContainerProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    informationGridItem?: GridItemProps;
    invoiceInformationGridItems?: GridItemProps;
    informationGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    shippingMethodGridItem?: GridItemProps;
    invoiceDateGridItem?: GridItemProps;
    termsGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    salespersonGridItem?: GridItemProps;
    dueDateGridItem?: GridItemProps;
    vatNumberGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    shippingAddressGridItem?: GridItemProps;
    invoiceNotesGridItem?: GridItemProps;
    billingAddressStyle?: InvoiceDetailsBillingAddressStyles;
    shippingAddressStyle?: InvoiceDetailsShippingAddressStyles;
    SalesOrderGridItem?: GridItemProps;
    SalesOrderText?: InjectableCss;
    SalesOrderTitle?: InjectableCss;
    icon?: TypographyProps;
    fieldsWrapper?: InjectableCss;
    textFieldStyle?: TextFieldProps;
}

export const informationStyles: InvoiceDetailsInformationStyles = {
    invoiceInformationGridContainer: {
        css: css`
            padding: 5px;
        `,
    },
    headingAndTextStyles: {
        heading: {
            variant: "h3",
            as: "h3",
            css: css`
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 5px;
            `,
        },
        text: {
            variant: "h4",
            as: "h4",
            css: css`
                font-weight: 400 !important;
                margin-top: 0 !important;
                @media print {
                    font-size: 12px;
                }
            `,
        },
    },
    informationGridItem: {
        width: 12,
    },
    invoiceInformationGridItems: {
        css: css`
            @media print {
                padding: 5px !important;
            }
        `,
    },
    statusGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    invoiceDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    termsGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    poNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    salespersonGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    shippingMethodGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    dueDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    vatNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    billingAddressGridItem: {
        width: 6,
    },
    shippingAddressGridItem: {
        width: 6,
    },
    invoiceNotesGridItem: {
        width: 12,
    },
    SalesOrderGridItem: {
        width: [6, 6, 6, 3, 3],
        css: css`
            display: block;
            & > h3 {
                margin-bottom: 0.02rem !important;
            }
            @media print {
                padding: 5px !important;
            }
        `,
    },
    SalesOrderText: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            margin-top: 0.2rem;
            @media print {
                font-size: ${TYPOGRAPHY.size.tiny};
                margin-top: 0 !important;
                a {
                    span {
                        color: ${COLORS.link.base} !important;
                    }
                }
            }
        `,
    },
    SalesOrderTitle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.bold};
            @media print {
                font-size: ${TYPOGRAPHY.size.tiny};
            }
        `,
    },
    icon: {
        css: css`
            cursor: pointer;
            svg {
                stroke: #444366;
            }

            &.addField {
                margin-top: 5px;
            }
        `,
    },
    fieldsWrapper: {
        css: css`
            display: flex;
            gap: 5px;
            align-items: center;
        `,
    },
    textFieldStyle: {
        cssOverrides: {
            inputSelect: css`
                border-radius: 0 !important;
                margin: -1px !important;
            `,
        },
    },
};

const styles = informationStyles;

const RMADetailsInformation = ({
    language,
    rmaDetails,
    updateDiscrepancyStatus,
    loadRMADetailsCollection,
    updateRMAStatusHandler,
    loadRMACollection,
    parameters,
    featureWidgets,
    isSecurityRolesAssigned,
}: Props) => {
    const [loading, setIsLoading] = React.useState(false);
    const [creditMemoLoading, setCreditMemoLoading] = React.useState(false);
    const [selectedStatus, setSelectedStatus] = React.useState(rmaDetails?.statusDisplay || "");
    const toasterContext = React.useContext(ToasterContext);
    const [creditMemoFields, setCreditMemoFields] = React.useState<string[]>([]);
    const [showSaveCreditMemoButton, setShowSaveCreditMemoButton] = React.useState(false);

    React.useEffect(() => {
        setSelectedStatus(rmaDetails?.statusDisplay || "");
    }, [rmaDetails?.statusDisplay]);

    React.useEffect(() => {
        setCreditMemoFields(
            rmaDetails?.creditMemo
                ? rmaDetails.creditMemo.split("|").filter((item: string) => item.trim() !== "")
                : [""],
        );
    }, [rmaDetails?.creditMemo]);

    React.useEffect(() => {
        if (!rmaDetails?.creditMemo && creditMemoFields.every(field => field.trim() === "")) {
            setShowSaveCreditMemoButton(false);
        }
    }, [rmaDetails?.creditMemo, creditMemoFields]);

    if (!rmaDetails) {
        return null;
    }

    const handleChange = (event: React.FormEvent<HTMLSelectElement>) => {
        if (event.currentTarget.value) {
            setIsLoading(true);
            setSelectedStatus(event.currentTarget.value);
            updateRMAStatusHandler({
                returnCode: rmaDetails.returnCode,
                status: event.currentTarget.value,
                ...(event.currentTarget.value === "Complete" && { closedDate: new Date().toISOString() }),
                creditMemo: rmaDetails.creditMemo,

                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("RMA status updated successfully"),
                        messageType: "success",
                    });
                    loadRMADetailsCollection({
                        rmaNumber: rmaDetails.returnCode,
                    });
                    setIsLoading(false);
                },
                onError: () => {
                    toasterContext.addToast({
                        body: translate("Failed to update RMA status"),
                        messageType: "danger",
                    });
                    setIsLoading(false);
                },
            });
        } else {
            setIsLoading(false);
        }
    };

    const handleCreditMemoChange = (index: number, value: string) => {
        const updatedFields = [...creditMemoFields];
        updatedFields[index] = value;
        setCreditMemoFields(updatedFields);
        setShowSaveCreditMemoButton(true);
    };

    const handleAddCreditMemoField = () => {
        setCreditMemoFields([...creditMemoFields, ""]);
        setShowSaveCreditMemoButton(true);
    };

    const handleDeleteCreditMemoField = (index: number) => {
        const updatedFields = creditMemoFields.filter((_, i) => i !== index);
        setCreditMemoFields(updatedFields);
        setShowSaveCreditMemoButton(true);
    };

    const updateCreditMemo = () => {
        const creditMemoValue = creditMemoFields.join("|");

        const maxLengthReached = creditMemoValue.length > 50;

        if (maxLengthReached) {
            toasterContext.addToast({
                body: translate("Credit memo cannot exceed 50 characters"),
                messageType: "danger",
            });
            return;
        }

        setCreditMemoLoading(true);
        updateRMAStatusHandler({
            returnCode: rmaDetails.returnCode,
            creditMemo: creditMemoValue,
            onSuccess: () => {
                toasterContext.addToast({
                    body: translate("Credit memo updated successfully"),
                    messageType: "success",
                });
                loadRMADetailsCollection({
                    rmaNumber: rmaDetails.returnCode,
                });
                setCreditMemoLoading(false);
                setShowSaveCreditMemoButton(false);
            },
            onError: () => {
                toasterContext.addToast({
                    body: translate("Failed to update credit memo"),
                    messageType: "danger",
                });
                setCreditMemoLoading(false);
            },
        });
    };

    return (
        <GridContainer {...styles.invoiceInformationGridContainer} data-test-selector="invoiceDetail_information">
            <GridItem {...styles.informationGridItem}>
                <GridContainer {...styles.informationGridContainer}>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("PO Number")}
                            text={rmaDetails.poNumber || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Return Code")}
                            text={rmaDetails.returnCode || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Return Auth Code")}
                            text={rmaDetails.returnAuthCode || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Return Type")}
                            text={rmaDetails.returnType || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Buyer Code")}
                            text={rmaDetails.buyerCode || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Buyer Region")}
                            text={rmaDetails.buyerRegion || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Buyer Email")}
                            text={rmaDetails.buyerEmail || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Stage")}
                            text={rmaDetails.stage || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.invoiceDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Issue Date")}
                            text={
                                rmaDetails.issueDate
                                    ? getLocalizedDateTime({
                                          dateTime: new Date(rmaDetails.issueDate),
                                          language,
                                      })
                                    : ""
                            }
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.invoiceDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Expire Date")}
                            text={
                                rmaDetails.expirationDate
                                    ? getLocalizedDateTime({
                                          dateTime: new Date(rmaDetails.expirationDate),
                                          language,
                                      })
                                    : ""
                            }
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Credit Line Total")}
                            text={rmaDetails.creditLineTotal || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Credit Recieved")}
                            text={rmaDetails.creditDateReceived || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Vendor")}
                            text={rmaDetails.vendorCode || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Vendor Restock Charge")}
                            text={rmaDetails.vendorLineRestockCharge || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Customer Restock Charge")}
                            text={rmaDetails.customerRestockCharge || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Orig PO System")}
                            text={rmaDetails.poSystemOrigin || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("PLOC Ship From")}
                            text={rmaDetails.shipFrom || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("PLOC Ship From Name")}
                            text={rmaDetails.shipFromName || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("User")}
                            text={rmaDetails.username || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Reason Code")}
                            text={rmaDetails.reasonCode || ""}
                        />
                    </GridItem>
                    <GridItem
                        {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}
                        css={css`
                            flex-direction: column;
                            margin-bottom: 15px !important;
                        `}
                    >
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Credit Memo")}
                            text={""}
                        />

                        {creditMemoFields?.map((field, index) => (
                            <StyledWrapper key={index} {...styles.fieldsWrapper}>
                                <TextField
                                    value={field}
                                    onChange={e => handleCreditMemoChange(index, e.target.value)}
                                    {...styles.textFieldStyle}
                                />
                                <Typography
                                    onClick={() => handleDeleteCreditMemoField(index)}
                                    data-test-selector={`deleteCreditMemoField_${index}`}
                                    {...styles.icon}
                                >
                                    <Trash2 />
                                </Typography>
                            </StyledWrapper>
                        ))}
                        {creditMemoFields.length < 5 && (
                            <Typography
                                {...styles.icon}
                                onClick={handleAddCreditMemoField}
                                data-test-selector="addCreditMemoField"
                                className="addField"
                            >
                                <Plus />
                            </Typography>
                        )}
                        {showSaveCreditMemoButton && (
                            <StyledWrapper
                                css={css`
                                    margin: 15px 0;
                                `}
                            >
                                <Button
                                    icon={{
                                        src: creditMemoLoading ? LoadingSpinner : "",
                                        position: "right",
                                    }}
                                    onClick={updateCreditMemo}
                                    disabled={creditMemoLoading}
                                >
                                    {translate("Update")}
                                </Button>
                            </StyledWrapper>
                        )}
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.invoiceDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Date Closed")}
                            text={
                                rmaDetails.closedDate
                                    ? getLocalizedDateTime({
                                          dateTime: new Date(rmaDetails.closedDate),
                                          language,
                                      })
                                    : ""
                            }
                        />
                    </GridItem>

                    {featureWidgets?.includes("RMA Status") ? null : (
                        <GridItem
                            {...mergeToNew(styles.invoiceInformationGridItems, styles.invoiceDateGridItem)}
                            style={{ flexDirection: "column" }}
                        >
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("RMA Status")}
                                text={""}
                            />
                            <Select
                                value={selectedStatus}
                                onChange={handleChange}
                                iconProps={{
                                    src: loading ? LoadingSpinner : ChevronDown,
                                    size: 20,
                                }}
                            >
                                <option value="">{translate("Select")}</option>
                                <option value="Open">{translate("Open")}</option>
                                <option value="In Progress">{translate("In Progress")}</option>
                                <option value="Pending">{translate("Pending")}</option>
                                <option value="Complete">{translate("Complete")}</option>
                            </Select>
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RMADetailsInformation),
    definition: {
        group: "Common",
        displayName: "RMA Details Information",
    },
};

export default widgetModule;
